import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Loader2, Search, MapPin, Clock, Wrench, Truck, 
  Phone, Star, Navigation, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider status configuration
const statusConfig = {
  online: { label: 'Online', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  busy: { label: 'Busy', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  offline: { label: 'Offline', color: 'bg-gray-400', textColor: 'text-gray-600', bgColor: 'bg-gray-50' }
};

function formatLastActive(lastActive) {
  if (!lastActive) return 'Never';
  const date = new Date(lastActive);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getProviderStatus(provider) {
  if (provider.is_online && provider.current_job_id) return 'busy';
  if (provider.is_online) return 'online';
  return 'offline';
}

export default function AssignProviderModal({ 
  isOpen, 
  onClose, 
  requestId, 
  serviceName,
  customerLocation,
  onAssigned 
}) {
  const { getAuthHeaders } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, online, offline, busy

  useEffect(() => {
    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/providers/available`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (providerId, providerName) => {
    setAssigning(providerId);
    try {
      const response = await axios.post(
        `${API}/admin/jobs/${requestId}/assign`,
        { provider_id: providerId },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(`Job assigned to ${providerName}!`, {
        description: `Navigation URL sent to provider. Auto-charge initiated.`
      });
      
      if (onAssigned) onAssigned(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign job');
    } finally {
      setAssigning(null);
    }
  };

  // Filter and search providers
  const filteredProviders = providers.filter(provider => {
    const status = getProviderStatus(provider);
    const matchesSearch = provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         provider.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Sort: online first, then busy, then offline
  const sortedProviders = [...filteredProviders].sort((a, b) => {
    const statusOrder = { online: 0, busy: 1, offline: 2 };
    return statusOrder[getProviderStatus(a)] - statusOrder[getProviderStatus(b)];
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Assign Provider
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Select a provider for: <span className="font-medium">{serviceName}</span>
          </p>
        </DialogHeader>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 py-3 border-b">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search providers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="provider-search"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'online', 'busy', 'offline'].map(status => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={filterStatus === status ? '' : 'text-gray-600'}
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : (
                  <>
                    <span className={`w-2 h-2 rounded-full mr-1.5 ${statusConfig[status]?.color || 'bg-gray-400'}`} />
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </>
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* Provider List */}
        <div className="flex-1 overflow-y-auto py-2 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : sortedProviders.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No providers found</p>
              <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
            </div>
          ) : (
            sortedProviders.map(provider => {
              const status = getProviderStatus(provider);
              const config = statusConfig[status];
              const TypeIcon = provider.user_type === 'mechanic' ? Wrench : Truck;
              
              return (
                <div
                  key={provider.user_id}
                  className={`p-4 rounded-lg border-2 transition-all ${config.bgColor} border-transparent hover:border-blue-300`}
                  data-testid={`provider-card-${provider.user_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Provider Photo */}
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                        {provider.picture ? (
                          <img src={provider.picture} alt={provider.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-7 h-7 text-gray-400" />
                        )}
                      </div>
                      {/* Status indicator */}
                      <span 
                        className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${config.color}`}
                        title={config.label}
                      />
                    </div>
                    
                    {/* Provider Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                        <Badge className={`${config.color} text-white text-xs`}>
                          {config.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {provider.user_type === 'mechanic' ? 'Mechanic' : 'Roadside Tech'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                        {provider.rating && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-yellow-500 fill-current" />
                            {provider.rating.toFixed(1)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatLastActive(provider.last_active)}
                        </span>
                        {provider.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {provider.phone}
                          </span>
                        )}
                      </div>
                      
                      {provider.current_job_id && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Currently on job: {provider.current_job_id}
                        </p>
                      )}
                    </div>
                    
                    {/* Assign Button */}
                    <Button
                      onClick={() => handleAssign(provider.user_id, provider.name)}
                      disabled={assigning === provider.user_id}
                      className="flex-shrink-0 bg-blue-600 hover:bg-blue-700"
                      data-testid={`assign-btn-${provider.user_id}`}
                    >
                      {assigning === provider.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Navigation className="w-4 h-4 mr-1" />
                          Assign
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Online: {providers.filter(p => getProviderStatus(p) === 'online').length}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" /> Busy: {providers.filter(p => getProviderStatus(p) === 'busy').length}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Offline: {providers.filter(p => getProviderStatus(p) === 'offline').length}
            </span>
          </div>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
